/*
  fd-cart-swipe.js — swipe-to-close for Dawn's cart drawer on touch devices.

  Loaded only from snippets/cart-drawer.liquid, and only when the merchant
  setting `fd_cart_swipe_close` is on. Attaches to the existing <cart-drawer>
  element and finishes every close through that element's own close() method,
  so focus return, the body overflow class and cart-drawer.js state stay
  correct. Dawn's own files are untouched.

  Touch events, not pointer events, on purpose: `.drawer__inner` is the cart's
  scroll container, and Chromium cancels the pointer stream one move into a
  horizontal drag inside a scroller (verified on Pixel 7 — pointerdown,
  pointermove, pointercancel). A non-passive touchmove that preventDefaults
  once the horizontal axis is locked is the only way to hold the gesture, and
  it doubles as the desktop guard: a mouse never fires touch events at all.

  Fails open: no touch support, no <cart-drawer>, or a viewport over 820px and
  the drawer behaves exactly as it does today.
*/
(function () {
  'use strict';

  if (typeof window === 'undefined') return;
  if (!('ontouchstart' in window) && !navigator.maxTouchPoints) return;

  var WIDTH_MQ = '(max-width: 820px)';
  var AXIS_LOCK = 9; // px of movement before we decide horizontal vs vertical
  var CLOSE_FRACTION = 0.3; // release past 30% of the panel width closes
  var CLOSE_VELOCITY = 0.5; // px/ms of rightward flick that closes regardless
  var VELOCITY_WINDOW = 120; // ms of samples used for the flick test
  var ANIM_FALLBACK = 400; // ms safety net if transitionend never fires
  var CLICK_SWALLOW = 150; // ms window in which a drag's trailing click is eaten

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function attach(drawer) {
    if (!drawer || drawer.dataset.fdCartSwipe === 'on') return;
    drawer.dataset.fdCartSwipe = 'on';

    var touchId = null; // identifier of the finger we are following, null when idle
    var panel = null;
    var startX = 0;
    var startY = 0;
    var originX = 0; // dx at the moment the horizontal axis was locked
    var x = 0;
    var width = 0;
    var decided = false;
    var dragging = false;
    var animating = false;
    var samples = [];
    var animTimer = 0;

    function findTouch(list) {
      for (var i = 0; i < list.length; i++) {
        if (list[i].identifier === touchId) return list[i];
      }
      return null;
    }

    function clearVisuals() {
      drawer.classList.remove('fd-cs-drag', 'fd-cs-anim');
      drawer.style.removeProperty('--fd-cs-x');
      drawer.style.removeProperty('--fd-cs-p');
    }

    function resetGesture() {
      touchId = null;
      panel = null;
      decided = false;
      dragging = false;
      samples = [];
      x = 0;
    }

    function setVars() {
      drawer.style.setProperty('--fd-cs-x', x + 'px');
      drawer.style.setProperty('--fd-cs-p', width ? String(Math.max(0, 1 - x / width)) : '1');
    }

    // Runs `done` once the settle/close transition has finished (or the
    // fallback timer fires). Under reduced motion there is no transition at
    // all: `done` runs straight away, which is the instant cut we want.
    function animateTo(targetX, targetP, done) {
      if (prefersReducedMotion() || !panel) {
        done();
        return;
      }
      var panelEl = panel;
      var finished = false;
      animating = true;
      drawer.classList.add('fd-cs-anim');

      function onEnd(evt) {
        if (evt.propertyName === 'transform') finish();
      }

      function finish() {
        if (finished) return;
        finished = true;
        window.clearTimeout(animTimer);
        panelEl.removeEventListener('transitionend', onEnd);
        animating = false;
        done();
      }

      panelEl.addEventListener('transitionend', onEnd);
      animTimer = window.setTimeout(finish, ANIM_FALLBACK);
      // next frame, so the transition has a start value to animate from
      window.requestAnimationFrame(function () {
        x = targetX;
        drawer.style.setProperty('--fd-cs-x', targetX + 'px');
        drawer.style.setProperty('--fd-cs-p', String(targetP));
      });
    }

    function springBack() {
      animateTo(0, 1, function () {
        clearVisuals();
        resetGesture();
      });
    }

    function commitClose() {
      animateTo(width, 0, function () {
        // Close through cart-drawer.js so focus return, the trap teardown and
        // the body class are Dawn's job. The panel is already parked at 100%
        // of its own width, which is exactly where `.drawer:not(.active)`
        // puts it, so dropping the override in the same frame causes no jump.
        if (typeof drawer.close === 'function') drawer.close();
        clearVisuals();
        resetGesture();
      });
    }

    function abort() {
      if (dragging) springBack();
      else {
        clearVisuals();
        resetGesture();
      }
    }

    function velocity() {
      if (samples.length < 2) return 0;
      var last = samples[samples.length - 1];
      var first = samples[0];
      for (var i = samples.length - 1; i >= 0; i--) {
        if (last.t - samples[i].t <= VELOCITY_WINDOW) first = samples[i];
        else break;
      }
      var dt = last.t - first.t;
      return dt > 0 ? (last.x - first.x) / dt : 0;
    }

    // A committed drag still produces a click on whatever was under the finger
    // (Check out, a quantity button). Eat exactly one, then stand down. The
    // window is deliberately short: the compatibility click lands in the same
    // tick as touchend, and anything later is a deliberate second tap.
    function swallowNextClick() {
      var timer = 0;
      var eat = function (evt) {
        window.clearTimeout(timer);
        document.removeEventListener('click', eat, true);
        // Only the drawer's own compatibility click is ours to eat; a tap
        // elsewhere (the bottom-nav Basket slot, say) must land (Wave 2c review M2).
        if (!drawer.contains(evt.target)) return;
        evt.preventDefault();
        evt.stopPropagation();
      };
      document.addEventListener('click', eat, true);
      timer = window.setTimeout(function () {
        document.removeEventListener('click', eat, true);
      }, CLICK_SWALLOW);
    }

    function onTouchStart(evt) {
      if (touchId !== null || animating) {
        // a second finger, or a gesture landing mid-animation: stand down
        if (touchId !== null && evt.touches.length > 1) {
          touchId = null; // null first so the next touchmove can't abort twice (review L1)
          abort();
        }
        return;
      }
      if (evt.touches.length > 1) return;
      if (!window.matchMedia(WIDTH_MQ).matches) return;
      if (!drawer.classList.contains('active')) return;

      var t = evt.changedTouches[0];
      var inner = t.target && t.target.closest ? t.target.closest('.drawer__inner') : null;
      if (!inner || !drawer.contains(inner)) return;

      touchId = t.identifier;
      panel = inner;
      startX = t.clientX;
      startY = t.clientY;
      decided = false;
      dragging = false;
      samples = [];
      x = 0;
    }

    function onTouchMove(evt) {
      if (touchId === null) return;
      if (evt.touches.length > 1) {
        // pinch: give the gesture back
        touchId = null;
        abort();
        return;
      }
      var t = findTouch(evt.touches);
      if (!t) return;
      var dx = t.clientX - startX;
      var dy = t.clientY - startY;

      if (!decided) {
        if (Math.abs(dx) < AXIS_LOCK && Math.abs(dy) < AXIS_LOCK) return;
        decided = true;
        if (Math.abs(dx) <= Math.abs(dy)) {
          // vertical wins — hand the gesture back to the scroller untouched.
          // Nothing was preventDefaulted, so the list scrolls normally.
          resetGesture();
          return;
        }
        dragging = true;
        originX = dx;
        width = panel.offsetWidth || 1;
        drawer.classList.add('fd-cs-drag');
      }

      if (!dragging) return;
      // Hold the gesture: without this Chromium hands it to the scroll
      // container and cancels our stream mid-drag.
      if (evt.cancelable) evt.preventDefault();
      x = Math.max(0, dx - originX);
      samples.push({ t: evt.timeStamp || Date.now(), x: x });
      if (samples.length > 12) samples.shift();
      setVars();
    }

    function onTouchEnd(evt) {
      if (touchId === null) return;
      if (findTouch(evt.touches)) return; // this finger is still down
      if (!dragging) {
        // a tap, or a vertical scroll — nothing to undo
        resetGesture();
        return;
      }
      swallowNextClick();
      var v = velocity();
      touchId = null;
      if (x >= width * CLOSE_FRACTION || v > CLOSE_VELOCITY) commitClose();
      else springBack();
    }

    function onTouchCancel() {
      if (touchId === null) return;
      touchId = null;
      abort();
    }

    // Delegated on <cart-drawer>: cart-drawer.js replaces #CartDrawer's inner
    // HTML on every cart update, so .drawer__inner is not a stable node.
    drawer.addEventListener('touchstart', onTouchStart, { passive: true });
    drawer.addEventListener('touchmove', onTouchMove, { passive: false });
    drawer.addEventListener('touchend', onTouchEnd);
    drawer.addEventListener('touchcancel', onTouchCancel);
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll('cart-drawer'), attach);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', boot);
})();
